import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Rocket, Users, Bus, AlertTriangle, ArrowRight, Check, Shield, Cloud, Zap, BarChart3, Lock, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import { usePageTracking } from '../hooks/usePageTracking';

export const LogicielsDeploiementPage = () => {
  usePageTracking();

  const softwares = [
    {
      id: 'innovagest',
      name: 'Innovagest',
      tagline: 'La gestion des absences et du remplacement, enfin maîtrisée',
      description: 'Innovagest centralise les déclarations d\'absences, l\'approbation et la recherche automatisée de remplaçants pour les centres de services scolaires. Conforme aux normes du secteur public québécois, opérationnel pour l\'année scolaire 2026-2027.',
      icon: <Users className="w-8 h-8" />,
      gradient: 'from-blue-500 to-cyan-500',
      modules: [
        {
          number: '01',
          title: 'Module Absence',
          description: 'Déclaration numérique, approbation en ligne, pièces justificatives et liens avec les transactions et rapports.',
          features: [
            'Espace employé intuitif',
            'Workflow d\'approbation',
            'Pièces justificatives'
          ]
        },
        {
          number: '02',
          title: 'Module Remplacement',
          description: 'Recherche automatisée de remplaçants — algorithme standard ou matching IA selon qualifications, disponibilités et ancienneté.',
          features: [
            'Classement intelligent',
            'Mode IA en option',
            'Affectation 1-clic'
          ]
        },
        {
          number: '03',
          title: 'Module Accès Sync-Intégration',
          description: 'Synchronisation des profils, horaires, calendrier, candidats et qualifications avec votre système GRH tiers.',
          features: [
            'Connecteurs GRH',
            'Mapping de champs',
            'Journal de synchronisation'
          ]
        }
      ],
      workflow: [
        { step: '1', title: 'Déclaration', desc: 'L\'employé déclare son absence en quelques clics' },
        { step: '2', title: 'Approbation', desc: 'Le gestionnaire reçoit et traite la demande' },
        { step: '3', title: 'Remplacement', desc: 'Le système propose des remplaçants classés' },
        { step: '4', title: 'Affectation', desc: 'Le remplaçant est affecté et notifié' }
      ],
      highlights: [
        { icon: <Shield />, title: 'Sécurité du secteur public', desc: 'Respect des normes gouvernementales québécoises' },
        { icon: <Lock />, title: 'Principe du moindre privilège', desc: 'Granularité des droits par rôle' },
        { icon: <Globe />, title: '100% francophone', desc: 'Interface et support entièrement en français' }
      ]
    },
    {
      id: 'innova-transport',
      name: 'Innova-Transport',
      tagline: 'La solution numérique du transport scolaire, conçue pour le Québec',
      description: 'Innova-Transport centralise toutes les demandes liées au transport scolaire — accès, informations, questions, commentaires, plaintes — dans une plateforme SaaS sécurisée, hébergée au Canada et conforme à la Loi 25.',
      icon: <Bus className="w-8 h-8" />,
      gradient: 'from-yellow-500 to-orange-500',
      modules: [
        {
          number: '01',
          title: 'Gestion centralisée des demandes',
          description: 'Un système de billetterie unique pour traiter accès, informations, questions, commentaires et plaintes — de l\'ouverture à la résolution.',
          features: [
            'Billetterie unifiée',
            'Suivi en temps réel',
            'Historique complet'
          ]
        },
        {
          number: '02',
          title: 'Priorisation automatisée',
          description: 'Règles configurables qui détectent l\'urgence (accès, plaintes, mots-clés) et redistribuent les demandes en temps réel.',
          features: [
            'Détection d\'urgence',
            'Redistribution automatique',
            'Files priorisées'
          ]
        },
        {
          number: '03',
          title: 'Assistant intelligent',
          description: 'Suggestions contextuelles et FAQ dynamique pour guider parents et citoyens pendant la rédaction de leurs demandes.',
          features: [
            'FAQ intelligente',
            'Suggestions contextuelles',
            'Aide à la rédaction'
          ]
        }
      ],
      workflow: [
        { step: '1', title: 'Parents & citoyens', desc: 'Soumettre, suivre, payer, dialoguer' },
        { step: '2', title: 'Personnel scolaire', desc: 'Files priorisées par école' },
        { step: '3', title: 'Transporteurs', desc: 'Demandes opérationnelles priorisées' },
        { step: '4', title: 'Équipe transport', desc: 'Pilotage, KPI, gabarits, audits' }
      ],
      highlights: [
        { icon: <Cloud />, title: '100% Hébergé au Canada', desc: 'Conforme aux exigences de localisation des données' },
        { icon: <Shield />, title: 'Loi 25 - Conformité totale', desc: 'Évaluation des facteurs relatifs à la vie privée fournie' },
        { icon: <Zap />, title: 'Disponibilité 24/7', desc: 'Plateforme toujours accessible' }
      ]
    },
    {
      id: 'gestevent',
      name: 'Gestevent',
      tagline: 'Plateforme de gestion des événements de violence et d\'intimidation',
      description: 'Plateforme numérique intégrée conçue pour soutenir les milieux scolaires dans la gestion des incidents d\'intimidation, de violence (VI) et de violence à caractère sexuel (VACS). Centralise et standardise les pratiques à l\'échelle de l\'organisation.',
      icon: <AlertTriangle className="w-8 h-8" />,
      gradient: 'from-red-500 to-pink-500',
      modules: [
        {
          number: '01',
          title: 'Réception de signalements',
          description: 'Permet aux utilisateurs de signaler des incidents de violence et d\'intimidation de manière sécurisée et confidentielle.',
          features: [
            'Signalement anonyme possible',
            'Accès facile (lien, QR code)',
            'Formulaire accessible à tous'
          ]
        },
        {
          number: '02',
          title: 'Journalisation des événements',
          description: 'Enregistrement détaillé des incidents et des interventions réalisées pour les victimes, auteurs et témoins.',
          features: [
            'Documentation complète',
            'Gabarits d\'intervention',
            'Traçabilité des actions'
          ]
        },
        {
          number: '03',
          title: 'Imputabilité et rapports',
          description: 'Génération automatisée de rapports sommaires pour les incidents VI ou VACS et facilitation de leur transmission.',
          features: [
            'Rapports automatisés',
            'Extraction de données',
            'Tableaux de bord analytiques'
          ]
        }
      ],
      workflow: [
        { step: '1', title: 'Signalement', desc: 'Réception du signalement (anonyme ou identifié)' },
        { step: '2', title: 'Évaluation', desc: 'Distinction automatique et classification' },
        { step: '3', title: 'Intervention', desc: 'Documentation des actions et mesures' },
        { step: '4', title: 'Suivi', desc: 'Rapports et analyse des tendances' }
      ],
      highlights: [
        { icon: <Lock />, title: 'Confidentialité absolue', desc: 'Gestion sécurisée des cas de violence sexuelle' },
        { icon: <BarChart3 />, title: 'Analyse de données', desc: 'Identification des tendances et zones à risque' },
        { icon: <Shield />, title: 'Conforme RGPD', desc: 'Protection des données personnelles et cybersécurité' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 network-lines opacity-5"></div>
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#FF6B35] opacity-20 blur-[150px] rounded-full"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-500 opacity-20 blur-[150px] rounded-full"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Rocket className="w-12 h-12 text-[#FF6B35]" />
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35]">
                Prêts à déployer
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 tracking-tight">
              Logiciels <span className="text-[#FF6B35]">Clés en Main</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-400 max-w-4xl mx-auto">
              Des solutions complètes, éprouvées et prêtes pour le déploiement immédiat. Conçues spécifiquement pour répondre aux besoins du secteur scolaire québécois.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Software Details */}
      {softwares.map((software, index) => (
        <section
          key={software.id}
          id={software.id}
          className={`py-20 relative overflow-hidden ${
            index % 2 === 0 ? 'bg-[#050505]' : 'bg-[#0A0A0A]'
          }`}
        >
          <div className="max-w-7xl mx-auto px-6">
            {/* Software Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-16 h-16 bg-gradient-to-br ${software.gradient} rounded-2xl flex items-center justify-center text-white shadow-lg`}>
                  {software.icon}
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
                    {software.name}
                  </h2>
                  <p className="text-gray-400">{software.tagline}</p>
                </div>
              </div>
              <p className="text-lg text-gray-300 max-w-4xl">
                {software.description}
              </p>
            </motion.div>

            {/* Modules */}
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              {software.modules.map((module, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#FF6B35]/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${software.gradient} rounded-lg flex items-center justify-center text-white font-black text-xl`}>
                      {module.number}
                    </div>
                    <h3 className="text-xl font-bold">{module.title}</h3>
                  </div>
                  <p className="text-gray-400 mb-4">{module.description}</p>
                  <div className="space-y-2">
                    {module.features.map((feature, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2 text-sm">
                        <div className="w-5 h-5 bg-[#FF6B35]/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-[#FF6B35]" />
                        </div>
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Workflow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <h3 className="text-2xl font-bold text-center mb-8">
                Comment ça <span className="text-[#FF6B35]">fonctionne</span>
              </h3>
              <div className="grid md:grid-cols-4 gap-6">
                {software.workflow.map((step, idx) => (
                  <div key={idx} className="text-center">
                    <div className={`w-16 h-16 bg-gradient-to-br ${software.gradient} rounded-full flex items-center justify-center mx-auto mb-4 text-white font-black text-2xl shadow-lg`}>
                      {step.step}
                    </div>
                    <h4 className="font-bold mb-2">{step.title}</h4>
                    <p className="text-sm text-gray-400">{step.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Highlights */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {software.highlights.map((highlight, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="glass rounded-xl p-6 text-center"
                >
                  <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4 text-[#FF6B35]">
                    {highlight.icon}
                  </div>
                  <h4 className="font-bold mb-2">{highlight.title}</h4>
                  <p className="text-sm text-gray-400">{highlight.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#FF6B35] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] inline-flex items-center gap-2"
              >
                Demander une démo de {software.name}
                <ArrowRight className="w-5 h-5" />
              </button>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Final CTA Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="absolute inset-0 network-lines opacity-5"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-black mb-6">
              Prêt à moderniser votre <span className="text-[#FF6B35]">Centre de services scolaire</span> ?
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              Demandez une démonstration personnalisée. Notre équipe vous accompagne du déploiement à la formation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="bg-[#FF6B35] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] inline-flex items-center justify-center gap-2"
              >
                Demander une consultation
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="tel:+14503599394"
                className="bg-transparent border-2 border-white/20 text-white font-semibold px-8 py-4 rounded-lg hover:border-white transition-all inline-flex items-center justify-center gap-2"
              >
                (450) 359-9394
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
